import type { ScanRules, Snapshot, SnapshotRecord } from '@jalsa/shared';

const DB_NAME = 'jalsa-duty';
const DB_VERSION = 1;
const STORE = 'keyval';
const KEY_ID = 'snapshot-key';
const SNAPSHOT_ID = 'snapshot';
const QUEUE_STORE = 'queue';

export interface StoredSnapshot {
  area: string;
  version: string;
  generatedAt: string;
  expiresAt: string;
  records: SnapshotRecord[];
  /** Leseregel für Kartencodes, wie sie der Server ausgeliefert hat. */
  scan?: ScanRules;
}

export interface QueuedEvent {
  clientEventId: string;
  mode: string;
  query?: string;
  personId?: string;
  result: string;
  reason?: string;
  clientAt: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'clientEventId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const request = fn(db.transaction(store, mode).objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

/**
 * Verschlüsselter Offline-Speicher.
 *
 * Der Schlüssel entsteht **auf dem Gerät** und verlässt es nie. Er wird
 * weder übertragen noch beim Anmelden ausgeliefert (Entscheidung aus P6).
 * Zusätzlich ist er `extractable: false`: selbst ein XSS-Fund kann ihn
 * benutzen, solange die Seite lebt, aber nicht herausschaffen.
 */
export class SnapshotStore {
  constructor(private readonly subtle: SubtleCrypto = crypto.subtle) {}

  private async key(): Promise<CryptoKey> {
    const existing = await tx<CryptoKey | undefined>(STORE, 'readonly', (s) => s.get(KEY_ID));
    if (existing) return existing;

    const created = await this.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
    // CryptoKey-Objekte lassen sich strukturiert klonen und damit direkt in
    // IndexedDB ablegen. Die rohen Bytes tauchen nirgends auf.
    await tx(STORE, 'readwrite', (s) => s.put(created, KEY_ID));
    return created;
  }

  async save(snapshot: Snapshot | StoredSnapshot): Promise<void> {
    const key = await this.key();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plain = new TextEncoder().encode(JSON.stringify(snapshot));
    const cipher = await this.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain);

    await tx(STORE, 'readwrite', (s) =>
      s.put({ iv, cipher, expiresAt: snapshot.expiresAt }, SNAPSHOT_ID),
    );
  }

  /**
   * Liest den Abzug. Abgelaufene Abzüge werden **gelöscht**, nicht nur
   * verschwiegen: nach der Haltbarkeit soll auf dem Gerät nichts
   * Entschlüsselbares mehr liegen.
   */
  async load(now: Date = new Date()): Promise<StoredSnapshot | null> {
    const row = await tx<{ iv: Uint8Array; cipher: ArrayBuffer; expiresAt: string } | undefined>(
      STORE,
      'readonly',
      (s) => s.get(SNAPSHOT_ID),
    );
    if (!row) return null;

    if (new Date(row.expiresAt) <= now) {
      await this.wipe();
      return null;
    }

    try {
      const key = await this.key();
      const iv = new Uint8Array(row.iv);
      const plain = await this.subtle.decrypt({ name: 'AES-GCM', iv }, key, row.cipher);
      return JSON.parse(new TextDecoder().decode(plain)) as StoredSnapshot;
    } catch {
      // Schlüssel weg oder Daten beschädigt. Dann ist der Abzug wertlos.
      await this.wipe();
      return null;
    }
  }

  /** Bei Gerätesperre und beim Abmelden: alles weg, auch der Schlüssel. */
  async wipe(): Promise<void> {
    await tx(STORE, 'readwrite', (s) => s.delete(SNAPSHOT_ID));
    await tx(STORE, 'readwrite', (s) => s.delete(KEY_ID));
  }

  // ── Warteschlange offline entschiedener Prüfungen ─────────────────────────

  /**
   * Offline entschiedene Prüfungen werden hier gesammelt, damit kein Einlass
   * verloren geht. Das Hochladen kommt in P9b, bis dahin sammelt das Gerät
   * sie nur, statt sie stillschweigend zu vergessen.
   */
  async enqueue(event: QueuedEvent): Promise<void> {
    await tx(QUEUE_STORE, 'readwrite', (s) => s.put(event));
  }

  async queued(): Promise<QueuedEvent[]> {
    return tx<QueuedEvent[]>(QUEUE_STORE, 'readonly', (s) => s.getAll());
  }

  async dequeue(clientEventId: string): Promise<void> {
    await tx(QUEUE_STORE, 'readwrite', (s) => s.delete(clientEventId));
  }
}
